import { addDays, type CalendarMap } from '@/lib/calendar';

// This module is the client-side mirror of hold.Pricer.Quote
// (internal/domain/hold/quote.go). A stay [checkin, checkout) is selectable
// iff Quote would price it:
//
//   - every night in [checkin, checkout) is `available`  (contiguity)
//   - `available_for_checkin` on the checkin date        (folds in CTA)
//   - `available_for_checkout` on the checkout date      (folds in CTD)
//   - nights >= min_stay_arrival(checkin)
//   - nights >= min_stay_through(n) for every night n
//   - nights <= max_stay(checkin), when set
//
// The calendar is STRICTER than Quote in exactly one direction: it subtracts
// active holds, which Quote never consults. That asymmetry is safe — the
// calendar may hide a night Quote would accept, never the reverse.

export interface CheckoutOptions {
  // undefined = no calendar data, therefore no constraint (the home page).
  // An empty Set = data present, nowhere to check out.
  selectable: Set<string> | undefined;
  // Nights to the earliest valid checkout; 0 when there is none.
  minNights: number;
}

export function checkoutOptions(
  map: CalendarMap | undefined,
  checkin: string,
): CheckoutOptions {
  if (!map) return { selectable: undefined, minNights: 1 };

  const start = map.get(checkin);
  if (!start || !start.available_for_checkin) {
    return { selectable: new Set(), minNights: 0 };
  }

  const selectable = new Set<string>();
  const maxStay = start.max_stay && start.max_stay > 0 ? start.max_stay : Infinity;
  let requiredNights = start.min_stay_arrival ?? 1;
  let minNights = 0;
  let nights = 0;
  let cursor = checkin;

  for (;;) {
    const night = map.get(cursor);
    // A night that cannot be sold ends the run: everything past it would need
    // the stay to pass THROUGH it.
    if (!night || !night.available) break;
    requiredNights = Math.max(requiredNights, night.min_stay_through ?? 1);
    nights += 1;
    if (nights > maxStay) break;

    const checkout = addDays(cursor, 1);
    const cell = map.get(checkout);
    // Past the end of the window there is no cell, so nothing to vouch for a
    // departure on that date (OH-120: with a 12-month request this is the last
    // night of the horizon, where no rates are published anyway).
    if (!cell) break;

    if (cell.available_for_checkout && nights >= requiredNights) {
      selectable.add(checkout);
      if (minNights === 0) minNights = nights;
    }
    cursor = checkout;
  }

  return { selectable, minNights };
}

// canCheckin reports whether a stay may START on this date. Beyond the
// server's own flag it requires at least one reachable checkout: a date you
// can arrive on but never leave from is a dead end that renders as an empty
// calendar after the click.
export function canCheckin(
  map: CalendarMap | undefined,
  date: string,
  today: string,
): boolean {
  if (date < today) return false;
  if (!map) return true;
  const cell = map.get(date);
  if (!cell || !cell.available_for_checkin) return false;
  return (checkoutOptions(map, date).selectable?.size ?? 0) > 0;
}

// buildCheckinSet precomputes canCheckin across the whole window. Called once
// per calendar payload (useMemo) rather than per rendered cell.
export function buildCheckinSet(map: CalendarMap, today: string): Set<string> {
  const out = new Set<string>();
  for (const iso of map.keys()) {
    if (canCheckin(map, iso, today)) out.add(iso);
  }
  return out;
}
